import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import contentService from '../services/contentService';
import api from '../services/api';

/**
 * Content Management Dashboard
 * 
 * Comprehensive content management including:
 * - Content pieces CRUD
 * - AI Content Generation
 * - Platform configuration
 * - Content calendar
 * - Analytics and performance tracking
 */

const CONTENT_TYPES = [
  { id: 'blog', name: 'Blog Post', icon: '📝', color: '#3b82f6' },
  { id: 'social', name: 'Social Media', icon: '📱', color: '#8b5cf6' },
  { id: 'email', name: 'Email', icon: '📧', color: '#ef4444' },
  { id: 'video', name: 'Video Script', icon: '🎬', color: '#f59e0b' },
  { id: 'landing', name: 'Landing Page', icon: '🖥️', color: '#10b981' },
  { id: 'ad', name: 'Advertisement', icon: '📢', color: '#ec4899' }
];

const CONTENT_STATUS = {
  draft: { color: '#6b7280', label: 'Draft' },
  'in-review': { color: '#f59e0b', label: 'In Review' },
  approved: { color: '#3b82f6', label: 'Approved' },
  published: { color: '#10b981', label: 'Published' },
  archived: { color: '#9ca3af', label: 'Archived' }
};

const ContentManagement = () => {
  const navigate = useNavigate();
  // State
  const [activeTab, setActiveTab] = useState('content');
  const [content, setContent] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // AI Generator states
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [aiForm, setAiForm] = useState({
    task_type: 'blog_post',
    topic: '',
    audience: '',
    tone: 'professional',
    keywords: ''
  });

  // Modal states
  const [showContentModal, setShowContentModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  // Filter states
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [contentForm, setContentForm] = useState({
    title: '',
    type: 'blog',
    content_body: '',
    meta_description: '',
    keywords: [],
    platform_id: '',
    scheduled_at: '',
    status: 'draft'
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [contentData, platformsData, typesData, analyticsResp] = await Promise.all([
        contentService.getContentPieces().catch(() => ({ content: [] })),
        contentService.getPlatforms().catch(() => ({ platforms: [] })),
        contentService.getContentTypes().catch(() => ({ types: [] })),
        contentService.getAnalytics().catch(() => null)
      ]);

      setContent(contentData.content || contentData || []);
      setPlatforms(platformsData.platforms || []);
      setContentTypes(typesData.types || CONTENT_TYPES);
      setAnalyticsData(analyticsResp);
    } catch (err) {
      console.error('Error fetching content data:', err);
      setError('Failed to load content data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // CRUD operations
  const handleCreateContent = async () => {
    setLoading(true);
    try {
      await contentService.createContentPiece(contentForm);
      setShowContentModal(false);
      setSuccess('Content created successfully');
      resetContentForm();
      fetchData();
    } catch (err) {
      setError('Failed to create content');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateContent = async () => {
    if (!selectedContent) return;
    setLoading(true);
    try {
      await contentService.updateContentPiece(selectedContent.id, contentForm);
      setShowContentModal(false);
      setSuccess('Content updated successfully');
      resetContentForm();
      fetchData();
    } catch (err) {
      setError('Failed to update content');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    setLoading(true);
    try {
      await contentService.deleteContentPiece(contentId);
      setSuccess('Content deleted');
      fetchData();
    } catch (err) {
      setError('Failed to delete content');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (contentId) => {
    setLoading(true);
    try {
      await contentService.updateContentPiece(contentId, { status: 'published' });
      setSuccess('Content published successfully');
      fetchData();
    } catch (err) {
      setError('Failed to publish content');
    } finally {
      setLoading(false);
    }
  };

  // Reset forms
  const resetContentForm = () => {
    setContentForm({
      title: '',
      type: 'blog',
      content_body: '',
      meta_description: '',
      keywords: [],
      platform_id: '',
      scheduled_at: '',
      status: 'draft'
    });
    setSelectedContent(null);
  };

  // AI Content Generation
  const handleAIGenerate = async (e) => {
    e.preventDefault();
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);

    try {
      const response = await api.post('/marketing/ai-agents/enhanced/content-creator', {
        task_type: aiForm.task_type,
        topic: aiForm.topic,
        audience: aiForm.audience,
        tone: aiForm.tone,
        keywords: aiForm.keywords.split(',').map(k => k.trim()).filter(k => k)
      });
      
      if (response.status === 202) {
        const taskId = response.data.task_id;
        pollTaskStatus(taskId);
      } else {
        if (response.data.result && !response.data.result.success) {
          const errorMsg = response.data.result.error || 'Failed to generate content';
          const details = response.data.result.details || '';
          setAiError(`${errorMsg}${details ? '\n\n' + details : ''}`);
          setAiLoading(false);
        } else {
          setAiResult(response.data);
          setAiLoading(false);
        }
      }
    } catch (err) {
      setAiError(err.response?.data?.error || 'Failed to generate content');
      setAiLoading(false);
    }
  };

  const pollTaskStatus = async (taskId) => {
    const maxAttempts = 60;
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await api.get(`/marketing/ai-agents/tasks/${taskId}`);
        const status = response.data.status;
        
        if (status === 'completed') {
          setAiResult(response.data);
          setAiLoading(false);
        } else if (status === 'failed') {
          setAiError(response.data.error || 'Content generation failed');
          setAiLoading(false);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 1000);
        } else {
          setAiError('Content generation timed out. Please try again.');
          setAiLoading(false);
        }
      } catch (err) {
        setAiError(err.response?.data?.error || 'Failed to check generation status');
        setAiLoading(false);
      }
    };
    
    setTimeout(poll, 1000);
  };

  const handleSaveAIContent = () => {
    if (aiResult?.result?.results) {
      const results = aiResult.result.results;
      let title = aiForm.topic;
      let body = '';
      
      if (results.email_data) {
        title = results.email_data.subject_line || aiForm.topic;
        body = typeof results.email_data.email_body === 'string' 
          ? results.email_data.email_body 
          : JSON.stringify(results.email_data.email_body, null, 2);
      } else if (results.content) {
        body = results.content;
      } else if (typeof results === 'string') {
        body = results;
      } else {
        body = JSON.stringify(results, null, 2);
      }
      
      // Map AI task type to content type
      const typeMap = {
        'blog_post': 'blog',
        'email_campaign': 'email',
        'social_media_content': 'social',
        'ad_copy': 'ad',
        'content_series': 'blog'
      };
      
      setContentForm({
        title: title,
        type: typeMap[aiForm.task_type] || 'blog',
        content_body: body,
        meta_description: '',
        keywords: aiForm.keywords.split(',').map(k => k.trim()).filter(k => k),
        platform_id: '',
        scheduled_at: '',
        status: 'draft'
      });
      setShowContentModal(true);
      setAiResult(null);
    }
  };

  // Filter content
  const filteredContent = content.filter(item => {
    const matchesType = filterType === 'all' || item.type === filterType;
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    const matchesSearch = !searchQuery || 
      item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.content_body?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });

  // Render stats cards
  const renderStatsCards = () => (
    <div className="stats-grid-4 mb-4">
      <div className="stat-card">
        <div className="stat-icon">📄</div>
        <div className="stat-content">
          <div className="stat-value">{content.length}</div>
          <div className="stat-label">Total Content</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon">✅</div>
        <div className="stat-content">
          <div className="stat-value">{content.filter(c => c.status === 'published').length}</div>
          <div className="stat-label">Published</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon">📝</div>
        <div className="stat-content">
          <div className="stat-value">{content.filter(c => c.status === 'draft').length}</div>
          <div className="stat-label">Drafts</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon">👁️</div>
        <div className="stat-content">
          <div className="stat-value">{analyticsData?.total_views?.toLocaleString() || '0'}</div>
          <div className="stat-label">Total Views</div>
        </div>
      </div>
    </div>
  );

  // Render content card
  const renderContentCard = (item) => {
    const type = CONTENT_TYPES.find(t => t.id === item.type) || CONTENT_TYPES[0];
    const status = CONTENT_STATUS[item.status] || CONTENT_STATUS.draft;

    return (
      <div key={item.id} className="content-card">
        <div className="content-card-header">
          <div className="content-type-badge" style={{ background: type.color }}>
            {type.icon} {type.name}
          </div>
          <div className="content-status" style={{ background: status.color }}>
            {status.label}
          </div>
        </div>

        <h3 className="content-title">{item.title || 'Untitled'}</h3>
        
        <p className="content-preview">
          {item.content_body?.substring(0, 120)}{item.content_body?.length > 120 ? '...' : ''}
        </p>

        {item.keywords?.length > 0 && (
          <div className="content-keywords">
            {item.keywords.slice(0, 3).map((kw, idx) => (
              <span key={idx} className="keyword-tag">{kw}</span>
            ))}
            {item.keywords.length > 3 && (
              <span className="keyword-more">+{item.keywords.length - 3}</span>
            )}
          </div>
        )}

        <div className="content-meta">
          <span className="meta-item">
            📅 {new Date(item.created_at || Date.now()).toLocaleDateString()}
          </span>
          {item.scheduled_at && (
            <span className="meta-item">
              ⏰ Scheduled: {new Date(item.scheduled_at).toLocaleDateString()}
            </span>
          )}
        </div>

        {item.status === 'published' && item.performance && (
          <div className="content-performance">
            <div className="perf-stat">
              <span className="perf-value">{item.performance.views || 0}</span>
              <span className="perf-label">Views</span>
            </div>
            <div className="perf-stat">
              <span className="perf-value">{item.performance.engagement || '0'}%</span>
              <span className="perf-label">Engagement</span>
            </div>
            <div className="perf-stat">
              <span className="perf-value">{item.performance.shares || 0}</span>
              <span className="perf-label">Shares</span>
            </div>
          </div>
        )}

        <div className="content-actions">
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => {
              setSelectedContent(item);
              setShowPreviewModal(true);
            }}
          >
            👁️ Preview
          </button>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => {
              setSelectedContent(item);
              setContentForm({ ...item });
              setShowContentModal(true);
            }}
          >
            ✏️ Edit
          </button>
          {item.status === 'approved' && (
            <button 
              className="btn btn-sm btn-success"
              onClick={() => handlePublish(item.id)}
            >
              🚀 Publish
            </button>
          )}
          <button 
            className="btn btn-sm btn-danger"
            onClick={() => handleDeleteContent(item.id)}
          >
            🗑️
          </button>
        </div>
      </div>
    );
  };

  // Render content modal
  const renderContentModal = () => (
    <div className="modal-overlay" onClick={() => setShowContentModal(false)}>
      <div className="modal-content modal-xl" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{selectedContent ? 'Edit Content' : 'Create Content'}</h3>
          <button className="btn-icon" onClick={() => setShowContentModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="content-editor-layout">
            <div className="editor-main">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  className="form-control form-control-lg"
                  value={contentForm.title}
                  onChange={e => setContentForm({ ...contentForm, title: e.target.value })}
                  placeholder="Enter content title..."
                />
              </div>

              <div className="form-group">
                <label>Content</label>
                <textarea
                  className="form-control content-textarea"
                  rows={15}
                  value={contentForm.content_body}
                  onChange={e => setContentForm({ ...contentForm, content_body: e.target.value })}
                  placeholder="Write your content here..."
                />
                <div className="content-stats">
                  <span>{contentForm.content_body.length} characters</span>
                  <span>{contentForm.content_body.split(/\s+/).filter(Boolean).length} words</span>
                  <span>~{Math.ceil(contentForm.content_body.split(/\s+/).filter(Boolean).length / 200)} min read</span>
                </div>
              </div>
            </div>

            <div className="editor-sidebar">
              <div className="sidebar-section">
                <h4>Content Type</h4>
                <div className="type-selector">
                  {CONTENT_TYPES.map(type => (
                    <button
                      key={type.id}
                      className={`type-btn ${contentForm.type === type.id ? 'active' : ''}`}
                      style={{ '--type-color': type.color }}
                      onClick={() => setContentForm({ ...contentForm, type: type.id })}
                    >
                      {type.icon} {type.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sidebar-section">
                <h4>Status</h4>
                <select
                  className="form-control"
                  value={contentForm.status}
                  onChange={e => setContentForm({ ...contentForm, status: e.target.value })}
                >
                  {Object.entries(CONTENT_STATUS).map(([key, val]) => (
                    <option key={key} value={key}>{val.label}</option>
                  ))}
                </select>
              </div>

              <div className="sidebar-section">
                <h4>Platform</h4>
                <select
                  className="form-control"
                  value={contentForm.platform_id}
                  onChange={e => setContentForm({ ...contentForm, platform_id: e.target.value })}
                >
                  <option value="">Select platform...</option>
                  {platforms.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div className="sidebar-section">
                <h4>Meta Description</h4>
                <textarea
                  className="form-control"
                  rows={3}
                  value={contentForm.meta_description}
                  onChange={e => setContentForm({ ...contentForm, meta_description: e.target.value })}
                  placeholder="SEO description..."
                  maxLength={160}
                />
                <small className="text-muted">{contentForm.meta_description.length}/160</small>
              </div>

              <div className="sidebar-section">
                <h4>Keywords</h4>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Add keywords (comma separated)"
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const value = e.target.value.replace(',', '').trim();
                      if (value && !contentForm.keywords.includes(value)) {
                        setContentForm({
                          ...contentForm,
                          keywords: [...contentForm.keywords, value]
                        });
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <div className="keywords-list">
                  {contentForm.keywords.map((kw, idx) => (
                    <span key={idx} className="keyword-tag">
                      {kw}
                      <button onClick={() => setContentForm({
                        ...contentForm,
                        keywords: contentForm.keywords.filter((_, i) => i !== idx)
                      })}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="sidebar-section">
                <h4>Schedule</h4>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={contentForm.scheduled_at}
                  onChange={e => setContentForm({ ...contentForm, scheduled_at: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowContentModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => {
              setContentForm({ ...contentForm, status: 'draft' });
              selectedContent ? handleUpdateContent() : handleCreateContent();
            }}
          >
            💾 Save Draft
          </button>
          <button 
            className="btn btn-primary"
            onClick={selectedContent ? handleUpdateContent : handleCreateContent}
            disabled={!contentForm.title}
          >
            {selectedContent ? '💾 Update' : '➕ Create'}
          </button>
        </div>
      </div>
    </div>
  );

  // Render preview modal
  const renderPreviewModal = () => {
    if (!selectedContent) return null;
    const type = CONTENT_TYPES.find(t => t.id === selectedContent.type) || CONTENT_TYPES[0];

    return (
      <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
        <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>👁️ Content Preview</h3>
            <button className="btn-icon" onClick={() => setShowPreviewModal(false)}>✕</button>
          </div>
          <div className="modal-body">
            <div className="preview-container">
              <div className="preview-type-badge" style={{ background: type.color }}>
                {type.icon} {type.name}
              </div>
              <h2 className="preview-title">{selectedContent.title}</h2>
              <div className="preview-meta">
                <span>📅 {new Date(selectedContent.created_at || Date.now()).toLocaleDateString()}</span>
                <span>📖 {Math.ceil(selectedContent.content_body?.split(/\s+/).filter(Boolean).length / 200)} min read</span>
              </div>
              <div className="preview-content">
                {selectedContent.content_body?.split('\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
              {selectedContent.keywords?.length > 0 && (
                <div className="preview-keywords">
                  {selectedContent.keywords.map((kw, idx) => (
                    <span key={idx} className="keyword-tag">{kw}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setShowPreviewModal(false)}>
              Close
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setShowPreviewModal(false);
                setContentForm({ ...selectedContent });
                setShowContentModal(true);
              }}
            >
              ✏️ Edit
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && !content.length) {
    return (
      <div>
        <div className="loading-spinner">Loading Content Management...</div>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Content Management</span>
      </nav>

      <div className="card">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              📝 Content Management
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Create, manage, and publish your content
            </p>
          </div>
          <div className="flex gap-2">
            <button 
              className="btn btn-primary"
              onClick={() => {
                resetContentForm();
                setShowContentModal(true);
              }}
            >
              ➕ New Content
            </button>
          </div>
        </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-danger mb-4">
          {error}
          <button className="btn-icon ml-auto" onClick={() => setError(null)}>✕</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success mb-4">
          {success}
          <button className="btn-icon ml-auto" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Stats */}
      {renderStatsCards()}

      {/* Tabs */}
      <div className="tabs mb-4">
        {['content', 'ai-generator', 'calendar', 'analytics'].map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'content' && '📄 Content'}
            {tab === 'ai-generator' && '🤖 AI Generator'}
            {tab === 'calendar' && '📅 Calendar'}
            {tab === 'analytics' && '📊 Analytics'}
          </button>
        ))}
      </div>

      {/* Content Tab */}
      {activeTab === 'content' && (
        <>
          {/* Filters */}
          <div className="filters-bar mb-4">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search content..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <span className="search-icon">🔍</span>
            </div>
            <select 
              className="filter-select"
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
            >
              <option value="all">All Types</option>
              {CONTENT_TYPES.map(t => (
                <option key={t.id} value={t.id}>{t.icon} {t.name}</option>
              ))}
            </select>
            <select 
              className="filter-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              {Object.entries(CONTENT_STATUS).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>

          {/* Content Grid */}
          <div className="content-grid">
            {filteredContent.length > 0 ? (
              filteredContent.map(renderContentCard)
            ) : (
              <div className="empty-state">
                <span style={{ fontSize: '4rem' }}>📝</span>
                <h3>No content found</h3>
                <p className="text-secondary">
                  {searchQuery || filterType !== 'all' || filterStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first content piece'}
                </p>
                <button className="btn btn-primary" onClick={() => setShowContentModal(true)}>
                  ➕ Create Content
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* AI Generator Tab */}
      {activeTab === 'ai-generator' && (
        <div className="ai-generator-container">
          <div className="ai-info-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '1.75rem' }}>🤖</span>
              <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>AI Content Creator</h3>
            </div>
            <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.875rem' }}>
              Generate high-quality marketing content using AI. The generated content will be added as a draft for your review.
            </p>
          </div>

          {aiError && (
            <div className="ai-error">
              <span style={{ fontSize: '1.25rem' }}>⚠️</span>
              <div>{aiError}</div>
            </div>
          )}

          {aiResult && aiResult.result && aiResult.result.success && (
            <div className="ai-success">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>✅</span>
                  <strong style={{ color: 'var(--success-700)' }}>Content Generated Successfully!</strong>
                </div>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveAIContent}
                >
                  📝 Save as Draft
                </button>
              </div>
              
              <div className="ai-result-meta">
                <span><strong>Agent:</strong> {aiResult.result.agent}</span>
                <span><strong>Type:</strong> {aiResult.result.task_type}</span>
                {aiResult.result.results?.confidence_score && (
                  <span className="confidence-badge">
                    {(aiResult.result.results.confidence_score * 100).toFixed(0)}% Confidence
                  </span>
                )}
              </div>
              
              {aiResult.result.results && (
                <div className="ai-result-content">
                  <h4>Generated Content:</h4>
                  <div className="ai-content-preview">
                    {(() => {
                      const results = aiResult.result.results;
                      
                      if (results.email_data) {
                        let emailBody = results.email_data.email_body;
                        let parsedEmailData = results.email_data;
                        
                        if (typeof emailBody === 'string') {
                          try {
                            const parsed = JSON.parse(emailBody);
                            if (parsed.subject_line || parsed.email_body) {
                              parsedEmailData = parsed;
                              emailBody = parsed.email_body;
                            }
                          } catch (e) {}
                        }
                        
                        return (
                          <div>
                            {parsedEmailData.subject_line && (
                              <div className="content-section">
                                <strong>Subject:</strong>
                                <p>{parsedEmailData.subject_line}</p>
                              </div>
                            )}
                            {emailBody && (
                              <div className="content-section">
                                <strong>Email Body:</strong>
                                <div className="email-preview">
                                  {typeof emailBody === 'object' ? JSON.stringify(emailBody, null, 2) : emailBody}
                                </div>
                              </div>
                            )}
                            {parsedEmailData.cta_text && (
                              <div className="content-section">
                                <strong>Call to Action:</strong>
                                <span className="cta-preview">{parsedEmailData.cta_text}</span>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      if (typeof results === 'string') return results;
                      if (results.content) return results.content;
                      return JSON.stringify(results, null, 2);
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleAIGenerate} className="ai-form">
            <div className="form-row-2">
              <div className="form-group">
                <label>Content Type *</label>
                <select
                  value={aiForm.task_type}
                  onChange={(e) => setAiForm({ ...aiForm, task_type: e.target.value })}
                  className="form-control"
                >
                  <option value="blog_post">📝 Blog Post</option>
                  <option value="email_campaign">📧 Email Campaign</option>
                  <option value="social_media_content">📱 Social Media Post</option>
                  <option value="ad_copy">📢 Ad Copy</option>
                  <option value="content_series">📚 Content Series</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tone *</label>
                <select
                  value={aiForm.tone}
                  onChange={(e) => setAiForm({ ...aiForm, tone: e.target.value })}
                  className="form-control"
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="friendly">Friendly</option>
                  <option value="authoritative">Authoritative</option>
                  <option value="playful">Playful</option>
                  <option value="luxury">Luxury</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Topic / Subject *</label>
              <input
                type="text"
                value={aiForm.topic}
                onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })}
                className="form-control"
                placeholder="e.g., Benefits of Email Marketing Automation"
                required
              />
            </div>

            <div className="form-group">
              <label>Target Audience</label>
              <input
                type="text"
                value={aiForm.audience}
                onChange={(e) => setAiForm({ ...aiForm, audience: e.target.value })}
                className="form-control"
                placeholder="e.g., Small business owners, B2B marketers"
              />
            </div>

            <div className="form-group">
              <label>Keywords (comma-separated)</label>
              <input
                type="text"
                value={aiForm.keywords}
                onChange={(e) => setAiForm({ ...aiForm, keywords: e.target.value })}
                className="form-control"
                placeholder="e.g., marketing, automation, productivity"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={aiLoading || !aiForm.topic}
              style={{ width: '100%' }}
            >
              {aiLoading ? '🤖 Generating Content...' : '✨ Generate AI Content'}
            </button>
          </form>
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="content-calendar">
          <div className="calendar-header">
            <h3>📅 Content Calendar</h3>
            <div className="calendar-nav">
              <button>‹</button>
              <span className="current-month">{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              <button>›</button>
            </div>
          </div>
          <div className="calendar-grid">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="calendar-day-header">{day}</div>
            ))}
            {Array.from({ length: 35 }).map((_, idx) => {
              const date = idx - new Date(new Date().getFullYear(), new Date().getMonth(), 1).getDay() + 1;
              const isCurrentMonth = date > 0 && date <= new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
              const isToday = isCurrentMonth && date === new Date().getDate();
              const scheduledContent = content.filter(c => {
                if (!c.scheduled_at) return false;
                const schedDate = new Date(c.scheduled_at);
                return schedDate.getDate() === date && 
                       schedDate.getMonth() === new Date().getMonth();
              });

              return (
                <div key={idx} className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}>
                  {isCurrentMonth && (
                    <>
                      <span className="day-number">{date}</span>
                      {scheduledContent.map(c => (
                        <div 
                          key={c.id} 
                          className="calendar-item"
                          style={{ background: CONTENT_TYPES.find(t => t.id === c.type)?.color }}
                          title={c.title}
                        >
                          {CONTENT_TYPES.find(t => t.id === c.type)?.icon} {c.title?.substring(0, 12)}...
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="calendar-legend">
            {CONTENT_TYPES.map(type => (
              <div key={type.id} className="legend-item">
                <span className="legend-color" style={{ background: type.color }}></span>
                <span>{type.icon} {type.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="grid-2-col gap-4">
          <div className="card">
            <h3 className="card-title">Performance by Type</h3>
            <div className="type-performance">
              {CONTENT_TYPES.map(type => {
                const typeContent = content.filter(c => c.type === type.id);
                const published = typeContent.filter(c => c.status === 'published').length;
                return (
                  <div key={type.id} className="type-stat">
                    <div className="type-info">
                      <span className="type-icon" style={{ color: type.color }}>{type.icon}</span>
                      <span>{type.name}</span>
                    </div>
                    <div className="type-counts">
                      <span>{typeContent.length} total</span>
                      <span>{published} published</span>
                    </div>
                    <div className="type-bar">
                      <div 
                        className="type-bar-fill" 
                        style={{ 
                          width: `${typeContent.length > 0 ? (published / typeContent.length) * 100 : 0}%`,
                          background: type.color 
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card">
            <h3 className="card-title">Top Performing Content</h3>
            <div className="top-content-list">
              {content
                .filter(c => c.status === 'published')
                .slice(0, 5)
                .map(c => (
                  <div key={c.id} className="top-content-item">
                    <span className="content-icon">
                      {CONTENT_TYPES.find(t => t.id === c.type)?.icon}
                    </span>
                    <div className="content-info">
                      <span className="content-name">{c.title}</span>
                      <span className="content-stats">
                        {c.performance?.views || 0} views • {c.performance?.engagement || 0}% engagement
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="card col-span-2">
            <h3 className="card-title">Content Workflow</h3>
            <div className="workflow-stats">
              {Object.entries(CONTENT_STATUS).map(([key, val]) => {
                const count = content.filter(c => c.status === key).length;
                return (
                  <div key={key} className="workflow-step">
                    <div className="step-dot" style={{ background: val.color }}></div>
                    <div className="step-info">
                      <span className="step-label">{val.label}</span>
                      <span className="step-count">{count} items</span>
                    </div>
                    <div className="step-arrow">→</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showContentModal && renderContentModal()}
      {showPreviewModal && renderPreviewModal()}

      <style>{`
        .stats-grid-4 {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        @media (max-width: 1024px) {
          .stats-grid-4 {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 640px) {
          .stats-grid-4 {
            grid-template-columns: 1fr;
          }
        }
        .stat-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: var(--gray-50);
          padding: 1.25rem;
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
        }
        .stat-icon {
          font-size: 2rem;
          line-height: 1;
        }
        .stat-content {
          display: flex;
          flex-direction: column;
        }
        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
          line-height: 1.2;
        }
        .stat-label {
          font-size: 0.875rem;
          color: var(--gray-600);
        }
        .mb-4 {
          margin-bottom: 1rem;
        }
        .tabs {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid var(--gray-200);
        }
        .tab {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          cursor: pointer;
          color: var(--gray-600);
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
        }
        .tab.active {
          color: var(--primary-600);
          border-bottom-color: var(--primary-600);
          font-weight: 600;
        }
        .filters-bar {
          display: flex;
          gap: 1rem;
          align-items: center;
        }
        .search-box {
          position: relative;
          flex: 1;
          max-width: 300px;
        }
        .search-box input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.5rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--radius-md);
        }
        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
        }
        .filter-select {
          padding: 0.75rem 1rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--radius-md);
          background: var(--gray-50);
          color: var(--gray-900);
        }
        .content-grid, .templates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1rem;
        }
        .content-card, .template-card {
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--gray-200);
        }
        .content-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        .content-type-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          color: white;
        }
        .content-status {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.7rem;
          color: white;
          text-transform: uppercase;
        }
        .content-title {
          margin: 0 0 0.75rem;
          font-size: 1.1rem;
        }
        .content-preview {
          color: var(--gray-600);
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }
        .content-keywords, .preview-keywords, .keywords-list {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }
        .keyword-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.25rem 0.5rem;
          background: var(--gray-100);
          border-radius: 4px;
          font-size: 0.75rem;
        }
        .keyword-tag button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          margin-left: 0.25rem;
        }
        .keyword-more {
          font-size: 0.75rem;
          color: var(--gray-500);
        }
        .content-meta {
          display: flex;
          gap: 1rem;
          font-size: 0.8rem;
          color: var(--gray-500);
          margin-bottom: 1rem;
        }
        .content-performance {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
          padding: 0.75rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
          margin-bottom: 1rem;
        }
        .perf-stat {
          text-align: center;
        }
        .perf-value {
          display: block;
          font-weight: 600;
        }
        .perf-label {
          font-size: 0.7rem;
          color: var(--gray-500);
        }
        .content-actions, .template-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .template-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .template-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }
        .template-name {
          margin: 0;
        }
        .template-type {
          font-size: 0.8rem;
          color: var(--gray-500);
        }
        .template-description {
          color: var(--gray-600);
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }
        .template-variables {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }
        .variables-label {
          font-size: 0.75rem;
          color: var(--gray-500);
          width: 100%;
        }
        .variable-tag {
          padding: 0.25rem 0.5rem;
          background: var(--primary-100);
          color: var(--primary-700);
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          font-family: monospace;
        }
        .content-editor-layout {
          display: grid;
          grid-template-columns: 1fr 300px;
          gap: 2rem;
        }
        .content-textarea, .template-textarea {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
        }
        .content-stats {
          display: flex;
          gap: 1rem;
          font-size: 0.75rem;
          color: var(--gray-500);
          margin-top: 0.5rem;
        }
        .sidebar-section {
          margin-bottom: 1.5rem;
        }
        .sidebar-section h4 {
          margin-bottom: 0.5rem;
          font-size: 0.875rem;
        }
        .type-selector {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .type-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-sm);
          background: var(--gray-50);
          cursor: pointer;
          text-align: left;
          font-size: 0.85rem;
          color: var(--gray-900);
        }
        .type-btn.active {
          border-color: var(--type-color);
          background: var(--gray-50);
        }
        .preview-container {
          max-width: 700px;
          margin: 0 auto;
        }
        .preview-type-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          color: white;
          margin-bottom: 1rem;
        }
        .preview-title {
          margin: 0 0 0.5rem;
        }
        .preview-meta {
          display: flex;
          gap: 1rem;
          font-size: 0.9rem;
          color: var(--gray-500);
          margin-bottom: 1.5rem;
        }
        .preview-content {
          line-height: 1.8;
        }
        .preview-content p {
          margin-bottom: 1rem;
        }
        .content-calendar {
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--gray-200);
        }
        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--gray-200);
        }
        .calendar-header h3 {
          margin: 0;
          font-size: 1.1rem;
          color: var(--gray-900);
        }
        .calendar-nav {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .calendar-nav button {
          width: 36px;
          height: 36px;
          border: 1px solid var(--gray-300);
          background: var(--gray-50);
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          color: var(--gray-700);
          transition: all 0.2s;
        }
        .calendar-nav button:hover {
          background: var(--gray-100);
          border-color: var(--gray-400);
        }
        .current-month {
          font-weight: 600;
          font-size: 1.1rem;
          min-width: 180px;
          text-align: center;
          color: var(--gray-900);
        }
        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          overflow: hidden;
        }
        .calendar-day-header {
          padding: 12px;
          text-align: center;
          font-weight: 600;
          font-size: 0.875rem;
          color: var(--gray-700);
          background: var(--gray-100);
          border-bottom: 1px solid var(--gray-200);
        }
        .calendar-day {
          min-height: 120px;
          padding: 8px;
          background: var(--gray-50);
          border-right: 1px solid var(--gray-200);
          border-bottom: 1px solid var(--gray-200);
          transition: background 0.2s;
        }
        .calendar-day:nth-child(7n) {
          border-right: none;
        }
        .calendar-day:hover {
          background: var(--gray-100);
        }
        .calendar-day.other-month {
          background: var(--gray-100);
          opacity: 0.5;
        }
        .calendar-day.today .day-number {
          background: var(--primary-600);
          color: white;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }
        .day-number {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--gray-900);
          margin-bottom: 6px;
        }
        .calendar-item {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          color: white;
          margin-bottom: 4px;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .calendar-item:hover {
          transform: translateX(2px);
          opacity: 0.9;
        }
        .calendar-legend {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
          padding: 16px;
          background: var(--gray-100);
          border-top: 1px solid var(--gray-200);
          margin-top: 0;
          border-radius: 0 0 8px 8px;
        }
        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: var(--gray-700);
        }
        .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 3px;
        }
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .type-performance {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .type-stat {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .type-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 120px;
        }
        .type-counts {
          display: flex;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: var(--gray-500);
          min-width: 120px;
        }
        .type-bar {
          flex: 1;
          height: 8px;
          background: var(--gray-200);
          border-radius: 4px;
          overflow: hidden;
        }
        .type-bar-fill {
          height: 100%;
          border-radius: 4px;
        }
        .top-content-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .top-content-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .content-icon {
          font-size: 1.5rem;
        }
        .content-info {
          display: flex;
          flex-direction: column;
        }
        .content-name {
          font-weight: 500;
        }
        .content-stats {
          font-size: 0.8rem;
          color: var(--gray-500);
        }
        .workflow-stats {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .workflow-step {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .step-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
        .step-info {
          display: flex;
          flex-direction: column;
        }
        .step-label {
          font-weight: 500;
          font-size: 0.9rem;
        }
        .step-count {
          font-size: 0.75rem;
          color: var(--gray-500);
        }
        .step-arrow {
          margin-left: 1rem;
          color: var(--gray-300);
        }
        .workflow-step:last-child .step-arrow {
          display: none;
        }
        .col-span-2 {
          grid-column: span 2;
        }
        .grid-2-col {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }
        .gap-4 {
          gap: 1.5rem;
        }
        @media (max-width: 768px) {
          .grid-2-col {
            grid-template-columns: 1fr;
          }
          .col-span-2 {
            grid-column: span 1;
          }
        }
        .empty-state {
          text-align: center;
          padding: 4rem;
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          grid-column: 1 / -1;
        }
        .empty-state h3 {
          margin: 1rem 0 0.5rem;
        }
        .variables-detected {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .form-row {
          display: flex;
          gap: 1rem;
        }
        .flex-1 {
          flex: 1;
        }
        .modal-xl {
          max-width: 1000px;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }
        .modal-content {
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        .modal-lg {
          max-width: 800px;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--gray-200);
          background: var(--gray-100);
          border-radius: var(--radius-lg) var(--radius-lg) 0 0;
        }
        .modal-header h3 {
          margin: 0;
          font-size: 1.25rem;
          color: var(--gray-900);
        }
        .modal-body {
          padding: 1.5rem;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--gray-200);
          background: var(--gray-100);
          border-radius: 0 0 var(--radius-lg) var(--radius-lg);
        }
        .btn-icon {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          color: var(--gray-500);
          padding: 0.25rem;
          line-height: 1;
          transition: color 0.2s;
        }
        .btn-icon:hover {
          color: var(--gray-900);
        }
        .form-group {
          margin-bottom: 1rem;
        }
        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: var(--gray-700);
          font-size: 0.875rem;
        }
        .form-control {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--radius-md);
          font-size: 0.875rem;
          background: var(--gray-50);
          color: var(--gray-900);
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-control:focus {
          outline: none;
          border-color: var(--primary-500);
          box-shadow: 0 0 0 3px var(--primary-100);
        }
        .form-control-lg {
          padding: 1rem 1.25rem;
          font-size: 1.125rem;
        }
        .content-textarea, .template-textarea {
          min-height: 300px;
          font-family: inherit;
          resize: vertical;
        }
        .content-stats {
          display: flex;
          gap: 1.5rem;
          margin-top: 0.5rem;
          font-size: 0.75rem;
          color: var(--gray-500);
        }
        .content-editor-layout {
          display: grid;
          grid-template-columns: 1fr 300px;
          gap: 1.5rem;
        }
        @media (max-width: 900px) {
          .content-editor-layout {
            grid-template-columns: 1fr;
          }
        }
        .editor-main {
          min-width: 0;
        }
        .editor-sidebar {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .sidebar-section {
          background: var(--gray-100);
          border-radius: var(--radius-md);
          padding: 1rem;
        }
        .sidebar-section h4 {
          margin: 0 0 0.75rem;
          font-size: 0.875rem;
          color: var(--gray-700);
        }
        .type-selector {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .text-muted {
          color: var(--gray-500);
        }
        .small {
          font-size: 0.8rem;
        }
        .ai-generator-container {
          max-width: 800px;
        }
        .ai-info-banner {
          background: var(--info-50);
          border: 1px solid var(--info-200);
          border-radius: var(--radius-lg);
          padding: 1.25rem;
          margin-bottom: 1.5rem;
        }
        .ai-error {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 1rem;
          background: var(--danger-100);
          color: var(--danger-700);
          border-radius: var(--radius-md);
          margin-bottom: 1.5rem;
          white-space: pre-wrap;
        }
        .ai-success {
          background: var(--success-50);
          border: 1px solid var(--success-200);
          border-radius: var(--radius-lg);
          padding: 1.25rem;
          margin-bottom: 1.5rem;
        }
        .ai-result-meta {
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
          padding: 0.75rem;
          background: var(--success-100);
          border-radius: var(--radius-sm);
          margin-bottom: 1rem;
          font-size: 0.875rem;
          color: var(--success-800);
        }
        .confidence-badge {
          background: var(--success-600);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-weight: 600;
        }
        .ai-result-content h4 {
          margin: 0 0 0.75rem;
          color: var(--gray-900);
        }
        .ai-content-preview {
          background: var(--gray-50);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-md);
          padding: 1rem;
          white-space: pre-wrap;
          font-size: 0.875rem;
          line-height: 1.6;
          max-height: 400px;
          overflow-y: auto;
        }
        .content-section {
          margin-bottom: 1rem;
        }
        .content-section strong {
          display: block;
          margin-bottom: 0.25rem;
          color: var(--gray-700);
        }
        .email-preview {
          background: var(--gray-100);
          padding: 1rem;
          border-radius: var(--radius-sm);
          margin-top: 0.5rem;
        }
        .cta-preview {
          display: inline-block;
          background: var(--primary-600);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: var(--radius-sm);
          margin-top: 0.25rem;
        }
        .ai-form {
          background: var(--gray-50);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
        }
        .form-row-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        @media (max-width: 640px) {
          .form-row-2 {
            grid-template-columns: 1fr;
          }
        }
        .btn-lg {
          padding: 1rem 1.5rem;
          font-size: 1rem;
        }
        .btn-sm {
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
        }
      `}</style>
      </div>
    </div>
  );
};

export default ContentManagement;
